import React, { useState, useEffect } from 'react'
import { Card, Table, Button, Tag, Modal, Descriptions, Image, Space, message, Tabs } from 'antd'
import { EyeOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { VITE_API_BASE as API_BASE } from '../config/apiConfig'

const { TabPane } = Tabs

const StudyBaseManage = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [stats, setStats] = useState({ totalBase: 0, pendingBase: 0, totalActivity: 0 })
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 })
  const [detailVisible, setDetailVisible] = useState(false)
  const [currentItem, setCurrentItem] = useState(null)
  const [activeTab, setActiveTab] = useState('pending')

  useEffect(() => {
    loadData()
    loadStats()
  }, [activeTab])

  const loadStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/base/admin/stats`)
      const result = await res.json()
      if (result.success) {
        setStats(result.data || { totalBase: 0, pendingBase: 0, totalActivity: 0 })
      }
    } catch (err) {
      // ignore
    }
  }

  const loadData = async (page = 1) => {
    setLoading(true)
    try {
      const status = activeTab === 'all' ? '' : activeTab
      const res = await fetch(`${API_BASE}/base/admin/list?page=${page}&status=${status}`)
      const result = await res.json()
      if (result.success) {
        setData(result.data || [])
        setPagination(prev => ({ ...prev, total: result.total || 0, current: page }))
      }
    } catch (err) {
      message.error('加载失败')
    }
    setLoading(false)
  }

  const showDetail = async (record) => {
    try {
      const res = await fetch(`${API_BASE}/base/admin/${record.id}`)
      const result = await res.json()
      if (result.success) {
        setCurrentItem(result.data)
        setDetailVisible(true)
      }
    } catch (err) {
      message.error('加载详情失败')
    }
  }

  const handleReview = async (id, status) => {
    try {
      const res = await fetch(`${API_BASE}/base/${id}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      })
      const result = await res.json()
      if (result.success) {
        message.success(status === 'approved' ? '已通过审核' : '已拒绝')
        setDetailVisible(false)
        loadData()
        loadStats()
      }
    } catch (err) {
      message.error('操作失败')
    }
  }

  const columns = [
    { title: '基地名称', dataIndex: 'name', width: 180 },
    { title: '类型', dataIndex: 'type', width: 100 },
    { title: '地区', width: 150,
      render: (_, r) => `${r.province} ${r.city}`
    },
    { title: '联系人', dataIndex: 'contact_name', width: 100 },
    { title: '电话', dataIndex: 'contact_phone', width: 130 },
    { title: '状态', dataIndex: 'status', width: 100,
      render: s => {
        const map = {
          pending: { color: 'orange', text: '待审核' },
          approved: { color: 'green', text: '已通过' },
          rejected: { color: 'red', text: '已拒绝' }
        }
        const { color, text } = map[s] || map.pending
        return <Tag color={color}>{text}</Tag>
      }
    },
    { title: '申请时间', dataIndex: 'created_at', width: 160,
      render: t => t?.substring(0, 16)
    },
    { title: '操作', width: 100, fixed: 'right',
      render: (_, record) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => showDetail(record)}>查看</Button>
      )
    }
  ]

  return (
    <div style={{ padding: 24 }}>
      <h2>🏛️ 研学基地管理</h2>

      {/* 统计卡片 */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <Card style={{ flex: 1 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 32, fontWeight: 'bold', color: '#667eea' }}>{stats.totalBase}</div>
            <div style={{ color: '#666' }}>入驻基地</div>
          </div>
        </Card>
        <Card style={{ flex: 1 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 32, fontWeight: 'bold', color: '#fa8c16' }}>{stats.pendingBase}</div>
            <div style={{ color: '#666' }}>待审核</div>
          </div>
        </Card>
        <Card style={{ flex: 1 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 32, fontWeight: 'bold', color: '#52c41a' }}>{stats.totalActivity}</div>
            <div style={{ color: '#666' }}>发布活动</div>
          </div>
        </Card>
      </div>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="待审核" key="pending" />
          <TabPane tab="已通过" key="approved" />
          <TabPane tab="已拒绝" key="rejected" />
          <TabPane tab="全部" key="all" />
        </Tabs>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            onChange: loadData
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title="基地详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={
          currentItem?.status === 'pending' ? (
            <Space>
              <Button type="primary" icon={<CheckOutlined />} onClick={() => handleReview(currentItem.id, 'approved')}>
                通过
              </Button>
              <Button danger icon={<CloseOutlined />} onClick={() => handleReview(currentItem.id, 'rejected')}>
                拒绝
              </Button>
            </Space>
          ) : null
        }
        width={700}
      >
        {currentItem && (
          <div>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="基地名称" span={2}>{currentItem.name}</Descriptions.Item>
              <Descriptions.Item label="基地类型">{currentItem.type}</Descriptions.Item>
              <Descriptions.Item label="容纳人数">{currentItem.capacity}人</Descriptions.Item>
              <Descriptions.Item label="所在地区" span={2}>{currentItem.province} {currentItem.city} {currentItem.address}</Descriptions.Item>
              <Descriptions.Item label="场地描述" span={2}>{currentItem.description || '无'}</Descriptions.Item>
              <Descriptions.Item label="联系人">{currentItem.contact_name}</Descriptions.Item>
              <Descriptions.Item label="联系电话">{currentItem.contact_phone}</Descriptions.Item>
              <Descriptions.Item label="适合年龄">{currentItem.suitable_age || '不限'}</Descriptions.Item>
              <Descriptions.Item label="申请时间">{currentItem.created_at?.substring(0, 16)}</Descriptions.Item>
              <Descriptions.Item label="营业执照" span={2}>
                {currentItem.business_license && (
                  <Image src={currentItem.business_license} width={150} />
                )}
              </Descriptions.Item>
            </Descriptions>
            
            {currentItem.photos && (
              <div style={{ marginTop: 16 }}>
                <b>基地照片：</b>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  {JSON.parse(currentItem.photos || '[]').map((img, i) => (
                    <Image key={i} src={img} width={100} height={100} style={{ objectFit: 'cover' }} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}

export default StudyBaseManage
